import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Loader2, Waypoints, Share2, Compass, Fingerprint, Eye, ChevronRight, Award } from 'lucide-react';
import { SENDAFooter } from '../components/SENDAFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODULES = [
    { id: 'senales_integracion', name: 'Señales de integración', icon: Waypoints, keys: ['nivel_integracion', 'consistencia_sistema'] },
    { id: 'transferencia_sistema', name: 'Transferencia del sistema', icon: Share2, keys: ['nivel_transferencia', 'coherencia_aplicada'] },
    { id: 'criterio_propio', name: 'Criterio propio', icon: Compass, keys: ['nivel_autonomia', 'claridad'] },
    { id: 'identidad_operativa', name: 'Identidad operativa', icon: Fingerprint, keys: ['nivel_alineacion', 'coherencia_identitaria'] },
    { id: 'vision_operativa', name: 'Visión operativa', icon: Eye, keys: ['nivel_claridad', 'aplicabilidad'] },
];

export default function ResumenCapitulo9() {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/profile`, { withCredentials: true });
                setProfile(data);
            } catch {} finally { setLoading(false); }
        })();
    }, []);

    if (loading) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    const summaries = profile?.ai_summaries || [];

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col">
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#EAE8E3]/50 sticky top-0 z-50">
                <div className="max-w-3xl mx-auto px-4 py-3">
                    <Link to="/usuario" className="flex items-center gap-2 text-[#3E5F6E] hover:text-[#2F4A57]"><ArrowLeft className="h-5 w-5" /><span className="text-sm font-medium">Volver al panel</span></Link>
                </div>
            </header>
            <main className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full">
                <div className="text-center mb-10">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#5FAF9D] to-[#3E5F6E] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"><Award className="h-10 w-10 text-white" /></div>
                    <h1 className="text-3xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="resumen-capitulo-9-title">Capítulo 9: Integración y cierre</h1>
                    <p className="text-[#616E7C]">Tu proceso SENDA completo</p>
                </div>
                <div className="space-y-4">
                    {MODULES.map(mod => {
                        const summary = summaries.find(s => s.module === mod.id);
                        const Icon = mod.icon;
                        return (
                            <Card key={mod.id} className="bg-white border border-[#DCE3E8] rounded-xl" data-testid={`resumen-9-${mod.id}`}>
                                <CardContent className="pt-5">
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 bg-[#3E5F6E]/10 rounded-lg flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-[#3E5F6E]" /></div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-sm text-[#1F2A30] font-['Outfit'] mb-1">{mod.name}</h3>
                                            {summary ? (
                                                <div className="space-y-1">
                                                    {mod.keys.map(k => summary[k] && (
                                                        <span key={k} className="inline-block mr-2 px-2 py-0.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded text-xs font-medium">{k.replace(/_/g, ' ')}: {summary[k]}</span>
                                                    ))}
                                                    {summary.insight_usuario && <p className="text-xs text-[#616E7C] italic mt-2">"{summary.insight_usuario}"</p>}
                                                </div>
                                            ) : <p className="text-xs text-[#9AA5B1]">Sin datos</p>}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {profile?.identidad_operativa && (
                    <Card className="mt-8 bg-gradient-to-br from-[#7A9BA8] to-[#3E5F6E] border-0 text-white rounded-xl" data-testid="identidad-operativa-snapshot">
                        <CardContent className="pt-6">
                            <h3 className="font-semibold text-lg font-['Outfit'] mb-4 flex items-center gap-2"><Fingerprint className="h-5 w-5" /> Tu identidad operativa</h3>
                            <div className="space-y-3">
                                {Object.entries(profile.identidad_operativa).map(([key, val]) => (
                                    <div key={key} className="bg-white/10 rounded-lg p-3">
                                        <p className="text-xs text-white/60 uppercase font-semibold mb-1">{key.replace(/_/g, ' ')}</p>
                                        <p className="text-sm text-white">{val}</p>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {profile?.vision_operativa && (
                    <Card className="mt-6 bg-gradient-to-br from-[#5FAF9D] to-[#3E5F6E] border-0 text-white rounded-xl" data-testid="vision-operativa-snapshot">
                        <CardContent className="pt-6">
                            <h3 className="font-semibold text-lg font-['Outfit'] mb-4 flex items-center gap-2"><Eye className="h-5 w-5" /> Tu visión operativa</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {Object.entries(profile.vision_operativa).map(([key, val]) => (
                                    <div key={key} className="bg-white/10 rounded-lg p-3">
                                        <p className="text-xs text-white/60 uppercase font-semibold mb-1">{key.replace(/_/g, ' ')}</p>
                                        <p className="text-sm text-white">{val}</p>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                <div className="mt-8 text-center">
                    <Link to="/usuario">
                        <Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="back-to-dashboard-from-cap9">
                            Volver al panel <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    </Link>
                </div>
            </main>
            <SENDAFooter />
        </div>
    );
}
